import { Shield, Lock, TrendingUp, FileCheck } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const trustFeatures = [
  {
    icon: Shield,
    title: "Regulated & Insured",
    description: "Legatia works with Swiss partner banks. Your funds are insured up to CHF 100,000 by Swiss deposit insurance.",
  },
  {
    icon: Lock,
    title: "Dedicated Custody Account",
    description: "Each child has a personal custody account in their name. The money legally belongs to them and is protected from creditors.",
  },
  {
    icon: TrendingUp,
    title: "Passive Management",
    description: "Diversified investments in ETFs tracking global indices (MSCI World). Low fees, maximum transparency.",
  },
  {
    icon: FileCheck,
    title: "Full Transparency",
    description: "Track investments in real-time. Annual reports. Clear, understandable fees.",
  },
];

const faqItems = [
  {
    question: "What happens to the money before age 18?",
    answer:
      "The money is invested in a dedicated custody account in the child's name. Parents can track growth but cannot withdraw funds. Only the child can access it at age 18, ensuring protection and long-term growth.",
  },
  {
    question: "Can I get my money back before age 18?",
    answer:
      "No. This is a key security feature of Legatia. Money is legally the child's property and cannot be withdrawn early. This ensures capital grows undisturbed until adulthood. In exceptional cases (serious illness, death), specific procedures apply.",
  },
  {
    question: "What are the risks?",
    answer:
      "Like any investment, ETFs fluctuate with the stock market. Short-term, values can go up or down. However, over 18 years, historical data shows consistent growth (7-8% annually on average). This is a long-term strategy, not speculation.",
  },
  {
    question: "Where does the 7-8% annual projection come from?",
    answer:
      "This projection is based on the historical performance of the MSCI World Index, a stock index tracking the world's 1,600 largest companies. Over the past 30 years (1994-2024), this index has shown an average annual growth of 7-8%. IMPORTANT: past performance does not guarantee future results. Markets fluctuate short-term but have historically shown consistent long-term growth (18 years in our case).",
  },
];

const TrustSectionEn = () => {
  return (
    <section className="section-padding bg-gradient-to-b from-purple-50/30 to-pink-50/20">
      <div className="container mx-auto max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="section-title">Trust moments and money with peace of mind</h2>
          <p className="subtitle-hero">Security, transparency, and regulation at the core</p>
        </div>

        {/* Trust Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {trustFeatures.map((feature, index) => (
            <div
              key={index}
              className="bg-card border-2 border-legatia-green/20 rounded-xl p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-legatia-green/10 flex items-center justify-center">
                    <feature.icon className="h-6 w-6 text-legatia-green" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="bg-card border-2 border-legatia-green/20 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h3>
          
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqItems.map((item, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border border-legatia-green/20 rounded-lg px-4"
              >
                <AccordionTrigger className="text-left font-semibold hover:text-legatia-green">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default TrustSectionEn;
